import type { GameSeat } from '../client/url.js';
import type { SavedDeckSelectionRef } from '../../server/protocol.js';
import { parseDeckUuid } from './api.js';

export type MatchDeckFlow = 'saved' | 'preset';
export type MatchDeckMode = 'private' | 'quick' | 'solo' | 'challenge';

export interface MatchDeckFlowStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function quickSearchStartedStorageKey(playerId: string): string {
  return `cards:quick-search:started:${playerId}`;
}

/** A fresh Lobby search must never inherit elapsed time from an abandoned run. */
export function clearQuickSearchStarted(
  storage: MatchDeckFlowStorage,
  playerId: string,
): void {
  storage.removeItem(quickSearchStartedStorageKey(playerId));
}

const MATCH_DECK_FLOW_VERSION = 2;
const MATCH_DECK_INTENT_VERSION = 1;
const LEGACY_MATCH_DECK_FLOW_VERSION = 1;

function versionedSeatKey(prefix: string, version: number, seat: GameSeat): string {
  return `${prefix}:v${version}:${encodeURIComponent(seat.room)}:${encodeURIComponent(seat.playerId)}`;
}

export function matchDeckFlowStorageKey(seat: GameSeat): string {
  return versionedSeatKey('cards:match-deck-flow', MATCH_DECK_FLOW_VERSION, seat);
}

export function matchDeckModeStorageKey(seat: GameSeat): string {
  return versionedSeatKey('cards:match-deck-mode', MATCH_DECK_FLOW_VERSION, seat);
}

export function matchDeckIntentStorageKey(seat: GameSeat): string {
  return versionedSeatKey('cards:match-deck-intent', MATCH_DECK_INTENT_VERSION, seat);
}

function legacyMatchDeckFlowStorageKey(seat: GameSeat): string {
  return versionedSeatKey('cards:match-deck-flow', LEGACY_MATCH_DECK_FLOW_VERSION, seat);
}

function legacyMatchDeckModeStorageKey(seat: GameSeat): string {
  return versionedSeatKey('cards:match-deck-mode', LEGACY_MATCH_DECK_FLOW_VERSION, seat);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.length && actual.every((key) => keys.includes(key));
}

function parseSelectionRef(value: unknown): SavedDeckSelectionRef {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('match deck intent deck должен быть объектом');
  }
  const object = value as Record<string, unknown>;
  if (!exactKeys(object, ['deckId', 'revision'])
    || !Number.isSafeInteger(object.revision)
    || (object.revision as number) <= 0) {
    throw new Error('некорректный match deck selection ref');
  }
  return {
    deckId: parseDeckUuid(object.deckId),
    revision: object.revision as number,
  };
}

export function rememberMatchDeckIntent(
  storage: MatchDeckFlowStorage,
  seat: GameSeat,
  deck: SavedDeckSelectionRef,
): void {
  const key = matchDeckIntentStorageKey(seat);
  const value = JSON.stringify({
    version: MATCH_DECK_INTENT_VERSION,
    deck: parseSelectionRef(deck),
  });
  storage.setItem(key, value);
  if (storage.getItem(key) !== value) throw new Error('Не удалось сохранить выбор колоды для матча');
}

/** Tampered local intent is discarded and never sent to the game server. */
export function readMatchDeckIntent(
  storage: MatchDeckFlowStorage,
  seat: GameSeat,
): SavedDeckSelectionRef | undefined {
  const key = matchDeckIntentStorageKey(seat);
  const raw = storage.getItem(key);
  if (raw === null) return undefined;
  try {
    const value = JSON.parse(raw) as unknown;
    if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Error('object');
    const object = value as Record<string, unknown>;
    if (!exactKeys(object, ['version', 'deck']) || object.version !== MATCH_DECK_INTENT_VERSION) {
      throw new Error('version');
    }
    return parseSelectionRef(object.deck);
  } catch {
    storage.removeItem(key);
    return undefined;
  }
}

export function clearMatchDeckIntent(storage: MatchDeckFlowStorage, seat: GameSeat): void {
  storage.removeItem(matchDeckIntentStorageKey(seat));
}

/** Fresh authenticated entries must choose before createClient opens a socket. */
export function matchDeckEntryRequiresPreflight(
  flow: MatchDeckFlow | undefined,
  _intent: SavedDeckSelectionRef | undefined,
  authenticated: boolean,
): boolean {
  return authenticated && flow === undefined;
}

export type MatchDeckEntryBootstrap =
  | Readonly<{ kind: 'started' }>
  | Readonly<{ kind: 'blocked' }>
  | Readonly<{
      kind: 'preflight';
      mode: MatchDeckMode;
      confirm(deck: SavedDeckSelectionRef): boolean;
      cancel(): void;
    }>;

/**
 * Testable transport boundary: a fresh authenticated entry cannot call `start`
 * until an explicit, still-authorized preflight confirmation wins exactly once.
 */
export function bootstrapMatchDeckEntry(
  input: Readonly<{
    mode: MatchDeckMode;
    flow: MatchDeckFlow | undefined;
    intent: SavedDeckSelectionRef | undefined;
    authenticated: boolean;
    canConfirm: () => boolean;
  }>,
  start: (selection?: SavedDeckSelectionRef) => void,
): MatchDeckEntryBootstrap {
  if (matchDeckEntryRequiresPreflight(input.flow, input.intent, input.authenticated)) {
    let open = true;
    return {
      kind: 'preflight',
      mode: input.mode,
      confirm(deck) {
        if (!open || !input.canConfirm()) return false;
        open = false;
        try {
          start(parseSelectionRef(deck));
          return true;
        } catch (error) {
          open = true;
          throw error;
        }
      },
      cancel() {
        open = false;
      },
    };
  }
  if (!matchDeckFlowCanStart(input.flow, input.authenticated)) return { kind: 'blocked' };
  start();
  return { kind: 'started' };
}

export function readMatchDeckFlow(
  storage: MatchDeckFlowStorage,
  seat: GameSeat,
): MatchDeckFlow | undefined {
  const key = matchDeckFlowStorageKey(seat);
  const value = storage.getItem(key);
  if (value === 'saved' || value === 'preset') return value;
  if (value !== null) storage.removeItem(key);

  const legacyKey = legacyMatchDeckFlowStorageKey(seat);
  const legacy = storage.getItem(legacyKey);
  if (legacy !== null) storage.removeItem(legacyKey);
  // Anonymous authority must remain frozen across deploys. An old account-bound
  // `saved` marker had no pre-entry intent, so deliberately return fresh and
  // require the new explicit preflight instead of silently starting transport.
  if (legacy === 'preset') {
    storage.setItem(key, legacy);
    if (storage.getItem(key) !== legacy) throw new Error('Не удалось перенести режим выбора колоды');
    return legacy;
  }
  return undefined;
}

export function matchDeckFlowCanStart(
  flow: MatchDeckFlow | undefined,
  authenticated: boolean,
): boolean {
  return flow !== 'saved' || authenticated;
}

export function rememberMatchDeckMode(
  storage: MatchDeckFlowStorage,
  seat: GameSeat,
  mode: MatchDeckMode,
): void {
  const key = matchDeckModeStorageKey(seat);
  storage.setItem(key, mode);
  if (storage.getItem(key) !== mode) throw new Error('Не удалось сохранить режим матча');
}

export function readMatchDeckMode(
  storage: MatchDeckFlowStorage,
  seat: GameSeat,
): MatchDeckMode | undefined {
  const key = matchDeckModeStorageKey(seat);
  const value = storage.getItem(key);
  if (value === 'private' || value === 'quick' || value === 'solo' || value === 'challenge') return value;
  if (value !== null) storage.removeItem(key);

  const legacyKey = legacyMatchDeckModeStorageKey(seat);
  const legacy = storage.getItem(legacyKey);
  if (legacy !== null) storage.removeItem(legacyKey);
  if (legacy === 'private' || legacy === 'quick' || legacy === 'solo') {
    rememberMatchDeckMode(storage, seat, legacy);
    return legacy;
  }
  return undefined;
}

/**
 * Freeze the deck authority chosen when a browser first enters a seat.
 *
 * A room seat is bound to either an account or the temporary anonymous preset
 * bridge on the server. Logging in later must not make an already anonymous
 * seat render account-owned controls that authority will reject.
 */
export function resolveMatchDeckFlow(
  storage: MatchDeckFlowStorage,
  seat: GameSeat,
  authenticatedAtEntry: boolean,
): MatchDeckFlow {
  const key = matchDeckFlowStorageKey(seat);
  const stored = readMatchDeckFlow(storage, seat);
  if (stored !== undefined) return stored;
  const flow: MatchDeckFlow = authenticatedAtEntry ? 'saved' : 'preset';
  storage.setItem(key, flow);
  if (storage.getItem(key) !== flow) throw new Error('Не удалось сохранить режим выбора колоды');
  return flow;
}

export function copyMatchDeckFlow(
  storage: MatchDeckFlowStorage,
  from: GameSeat,
  to: GameSeat,
): MatchDeckFlow {
  const flow = resolveMatchDeckFlow(storage, from, false);
  const key = matchDeckFlowStorageKey(to);
  storage.setItem(key, flow);
  if (storage.getItem(key) !== flow) throw new Error('Не удалось перенести режим выбора колоды');
  const mode = readMatchDeckMode(storage, from);
  if (mode !== undefined) rememberMatchDeckMode(storage, to, mode);
  const intent = readMatchDeckIntent(storage, from);
  if (intent !== undefined) rememberMatchDeckIntent(storage, to, intent);
  return flow;
}

export function clearMatchDeckFlow(storage: MatchDeckFlowStorage, seat: GameSeat): void {
  storage.removeItem(matchDeckFlowStorageKey(seat));
  storage.removeItem(matchDeckModeStorageKey(seat));
  storage.removeItem(legacyMatchDeckFlowStorageKey(seat));
  storage.removeItem(legacyMatchDeckModeStorageKey(seat));
  clearMatchDeckIntent(storage, seat);
}

/** Clears entry and promoted-room bindings together on cancel or terminal rejection. */
export function clearMatchDeckFlowSeats(
  storage: MatchDeckFlowStorage,
  entrySeat: GameSeat,
  promotedSeat?: GameSeat,
): void {
  clearMatchDeckFlow(storage, entrySeat);
  if (promotedSeat !== undefined
    && (promotedSeat.room !== entrySeat.room || promotedSeat.playerId !== entrySeat.playerId)) {
    clearMatchDeckFlow(storage, promotedSeat);
  }
}
